import { css, Theme } from '@emotion/react';
import { Box, Typography } from '@material-ui/core';
import { KeyboardBackspace } from '@material-ui/icons';
import { useParams } from 'react-router-dom';

import { formatPhoneNumber } from '@jebel/utils';

import { CarnivalLayout } from 'shared/components/layouts';
import { CommunityAvatar } from 'shared/components/symbols';
import { ButtonLink, FeedSkeleton, LoadingOverlay } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { DISCOUNTS, SUPPORTERS } from 'shared/constants/sidebar';
import { CommunitySidebar } from 'shared/features/community-sidebar';
import { useCommunityProfile } from 'shared/hooks';
import { getBusinessAddressShort } from 'shared/utils/address';

interface Params {
  /** ID of the community?. */
  community: string;
}

export function ExplorerNationCommunityProfilePage() {
  const { community: id } = useParams<Params>();

  const { data: community, loading } = useCommunityProfile({
    variables: { id },
  });

  return (
    <CarnivalLayout>
      <CarnivalLayout.Content css={contentCSS}>
        <LoadingOverlay loading={loading} overlay={<FeedSkeleton />}>
          <ButtonLink
            variant="text"
            size="small"
            startIcon={<KeyboardBackspace />}
            to={ROUTES.user.explorer.index}
          >
            Go Back
          </ButtonLink>

          <Box component="header" css={headerCSS}>
            <CommunityAvatar variant="rounded" css={avatarCSS} size={160} community={community} />

            <Box css={headerContentCSS}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {community?.name}
                </Typography>

                <Typography variant="h5">{community?.fullName}</Typography>
              </Box>

              <Typography variant="body2" color="textSecondary">
                {community?.type?.name}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2">{community?.description}</Typography>

          {community?.email && (
            <Box>
              <Typography variant="h6">Contact Email</Typography>
              <Typography>{community.email}</Typography>
            </Box>
          )}

          {community?.phoneNumber?.number && (
            <Box>
              <Typography variant="h6">Contact Phone Number</Typography>

              <Typography>{formatPhoneNumber(community.phoneNumber.number)}</Typography>
            </Box>
          )}

          {community?.address && (
            <Box>
              <Typography variant="h6">Address</Typography>

              <Typography>{getBusinessAddressShort(community.address)}</Typography>
            </Box>
          )}
        </LoadingOverlay>
      </CarnivalLayout.Content>

      <CarnivalLayout.Sidebar>
        <CommunitySidebar items={[SUPPORTERS, DISCOUNTS]} />
      </CarnivalLayout.Sidebar>
    </CarnivalLayout>
  );
}

const contentCSS = css`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 1rem;
`;

const headerCSS = (theme: Theme) => css`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 1rem;

  ${theme.breakpoints.down('md')} {
    flex-direction: column;
    align-items: start;
  }
`;

const headerContentCSS = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const avatarCSS = (theme: Theme) => css`
  --avatar-size: 10rem;

  min-width: var(--avatar-size);
  min-height: var(--avatar-size);
  border: solid 1px;
  border-color: ${theme.palette.divider};

  ${theme.breakpoints.down('md')} {
    --avatar-size: 12rem;

    width: 100%;
  }
`;
